export const DEFAULT_COMMAND_BRAIN = {
  pageAliases: [
    { label: "Dashboard", path: "/", aliases: ["home", "main", "control room"] },
    { label: "Nexo Assistant", path: "/nexo-assistant", aliases: ["assistant", "voice assistant", "nexo settings"] },
    { label: "Bloomberg Watch", path: "/bloomberg-watch", aliases: ["bloomberg", "news watch", "market news"] },
    { label: "Bloomberg Terminal", path: "/bloomberg-terminal", aliases: ["terminal", "bloomberg terminal"] },
    { label: "Option Decay", path: "/option-decay", aliases: ["options", "decay"] },
    { label: "Expiry Decay Desk", path: "/expiry-decay-desk", aliases: ["expiry", "expiry desk", "sensex terminal", "itm terminal"] },
    { label: "Market Reaction", path: "/market-reaction", aliases: ["market", "reaction"] },
    { label: "Strike Market Reaction", path: "/strike-market-reaction", aliases: ["strike market", "strike reaction"] },
    { label: "World Monitor", path: "/world-monitor", aliases: ["world", "global monitor"] },
  ],
  actionAliases: [
    { action: "start", aliases: ["start", "begin", "run", "launch", "activate", "enable", "turn on", "fire up"] },
    { action: "stop", aliases: ["stop", "pause", "halt", "disable", "turn off", "shut down"] },
    { action: "read", aliases: ["read", "read out", "tell me", "what is", "show", "explain", "summarize", "summary"] },
    { action: "click", aliases: ["click", "press", "tap", "hit", "select"] },
    { action: "navigate", aliases: ["open", "go to", "shift to", "switch to", "move to", "navigate to"] },
  ],
  strategyTerms: [
    { term: "call sellers", aliases: ["ce sellers", "call writers", "ce writers", "call side sellers"], meaning: "Call-side seller metric" },
    { term: "put sellers", aliases: ["pe sellers", "put writers", "pe writers", "put side sellers"], meaning: "Put-side seller metric" },
    { term: "total volume", aliases: ["volume", "total traded volume", "combined volume"], meaning: "Visible total volume metric" },
    { term: "open interest", aliases: ["oi", "total oi", "ce oi", "pe oi"], meaning: "Options open interest metric" },
    { term: "PCR", aliases: ["put call ratio", "call put ratio", "pcr ratio"], meaning: "Put-call ratio" },
  ],
  learnedTerms: [],
  pageSnapshots: [],
};

export function normalizeBrainText(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/[^\w\s./',-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function cleanArray(value) {
  return Array.isArray(value)
    ? value.map(item => String(item || "").trim()).filter(Boolean)
    : [];
}

function mergeList(defaults, incoming, keyName) {
  const byKey = new Map();
  const base = Array.isArray(defaults) ? defaults : [];
  const extra = Array.isArray(incoming) ? incoming : [];
  [...base, ...extra].forEach(item => {
    if (!item || typeof item !== "object") return;
    const key = normalizeBrainText(item[keyName] || item.label || item.term || item.action || item.path);
    if (!key) return;
    const existing = byKey.get(key) || {};
    byKey.set(key, {
      ...existing,
      ...item,
      aliases: Array.from(new Set([...cleanArray(existing.aliases), ...cleanArray(item.aliases)])),
    });
  });
  return Array.from(byKey.values());
}

export function mergeCommandBrain(remote = {}) {
  return {
    pageAliases: mergeList(DEFAULT_COMMAND_BRAIN.pageAliases, remote.pageAliases, "path"),
    actionAliases: mergeList(DEFAULT_COMMAND_BRAIN.actionAliases, remote.actionAliases, "action"),
    strategyTerms: mergeList(DEFAULT_COMMAND_BRAIN.strategyTerms, remote.strategyTerms, "term"),
    learnedTerms: Array.isArray(remote.learnedTerms) ? remote.learnedTerms.filter(item => item && typeof item === "object") : [],
    pageSnapshots: Array.isArray(remote.pageSnapshots) ? remote.pageSnapshots.filter(item => item && typeof item === "object") : [],
  };
}

export function aliasesForAction(brain, action) {
  const merged = mergeCommandBrain(brain);
  const item = merged.actionAliases.find(entry => normalizeBrainText(entry.action) === normalizeBrainText(action));
  return item ? [item.action, ...cleanArray(item.aliases)] : [];
}

export function findActionByBrain(brain, text) {
  const haystack = normalizeBrainText(text);
  if (!haystack) return "";
  const merged = mergeCommandBrain(brain);
  const found = merged.actionAliases.find(entry =>
    [entry.action, ...cleanArray(entry.aliases)].some(alias => {
      const needle = normalizeBrainText(alias);
      return needle && new RegExp(`\\b${needle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`).test(haystack);
    })
  );
  return found?.action || "";
}

export function findPageByBrain(brain, text) {
  const haystack = normalizeBrainText(text)
    .replace(/\b(can you|please|nexo|i want to|take me|let's go|lets go|open|go to|shift to|switch to|move to|navigate to|load|launch)\b/g, " ")
    .replace(/\b(tab|page|screen|section|window|terminal|desk|panel)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!haystack) return null;

  const merged = mergeCommandBrain(brain);
  return merged.pageAliases.find(page =>
    [page.label, page.path, ...cleanArray(page.aliases)].some(alias => {
      const needle = normalizeBrainText(alias);
      return needle && (haystack === needle || haystack.includes(needle) || needle.includes(haystack));
    })
  ) || null;
}

export function expandStrategyTerms(brain, text) {
  let expanded = String(text || "");
  const haystack = normalizeBrainText(text);
  const merged = mergeCommandBrain(brain);
  [...merged.strategyTerms, ...merged.learnedTerms].forEach(item => {
    const canonical = item.term || item.label || item.meaning;
    if (!canonical) return;
    cleanArray(item.aliases).forEach(alias => {
      const needle = normalizeBrainText(alias);
      if (needle && haystack.includes(needle) && !normalizeBrainText(expanded).includes(normalizeBrainText(canonical))) {
        expanded += ` ${canonical}`;
      }
    });
  });
  return expanded;
}

export function findLearnedResponse(brain, text) {
  const haystack = normalizeBrainText(text);
  if (!haystack) return "";
  const merged = mergeCommandBrain(brain);
  const item = merged.learnedTerms.find(entry =>
    [entry.term, entry.label, ...cleanArray(entry.aliases)].some(alias => {
      const needle = normalizeBrainText(alias);
      return needle && (haystack === needle || haystack.includes(needle));
    })
  );
  const meaning = String(item?.meaning || "").trim();
  const match = meaning.match(/^(say|reply|respond)\s*:\s*(.+)$/i);
  return match?.[2]?.trim() || "";
}
